import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button, type ButtonVariant } from '@clay-run/glaze/button';
import { InputField } from '@clay-run/glaze/input-field';
import { Popover, PopoverButton, PopoverPanel } from '@clay-run/glaze/popover';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../_shared/hooks';
import { setSequenceFlow } from '../mutations';
import { channelLabel } from '../sequence-readiness';
import { applySequenceFlowIdToCache } from '../sequence-identity-cache';
import { usedInPlacesText } from '../usage-gate';
import type { LauncherChannel } from '../schemas';
import { settingsDraftForReopen } from '../settings-draft-restore';
import { useUsageSidebarStore, type UsageSidebarOrigin } from '../usage-sidebar-store';
import { saveSequenceSettingsWithGate } from './SequenceSenderPopover';

/**
 * playbooks-inflection-wiring (2026-09-17) — the free-text flow-id control for
 * every NON-Gong channel. Fixes a real bug: `sequence-readiness.ts` requires a
 * `flowId` on every channel, but the only pre-existing write path
 * (`PATCH /sequences/:id/flow`) refused any id not in the Gong catalog, so an
 * Inflection or Terra Co-Pilot sequence could never become "Ready" — see this
 * plan's TL;DR item 1.
 *
 * Structural copy of `SequenceSenderPopover.tsx`, minus the sender
 * vocabulary (this collects one free-text field, not a token/email choice).
 * At the DOORWAYS (`playId` undefined) it writes in place with the
 * `usedInPlacesText(usageCount)` caption for discoverability only (TL;DR
 * item 6 of that plan — unchanged).
 *
 * Plan 2026-09-28-006 U4 (rulebook R5, R6) — inside a PLAY it goes through
 * the same usage gate as every other sequence edit
 * (`SequenceSenderPopover.tsx#saveSequenceSettingsWithGate`), opening the
 * Save menu with a `{ field: 'flow', flowId }` pending when anything but
 * this group uses the sequence.
 *
 * Rendered ONLY for `channel !== 'gong_engage'` — Gong keeps its existing
 * browse-panel doorways (`LauncherGongFlowBrowse`) untouched.
 */
export function SequenceFlowIdPopover({
  sequenceId,
  channel,
  currentFlowId,
  usageCount,
  variant = 'secondary',
  size = 'xs',
  onSaved,
  playId,
  playbookId,
  usageOrigin,
  sequenceName,
  owningPlayId = null,
  archivedInLibrary = false,
}: {
  sequenceId: string;
  channel: LauncherChannel;
  /** The sequence's stored `flow_id`, or `null` if never set. */
  currentFlowId: string | null;
  usageCount: number;
  variant?: ButtonVariant;
  size?: 'xs' | 'sm';
  onSaved?: (flowId: string) => void;
  /**
   * Plan 2026-09-25-002, U7 (Decision 21) — passed from the play flow header
   * (`SequencesStep.tsx`) so this save's `?playId=` lets the server's
   * ownership guard confirm the addressed sequence belongs to this play.
   * `undefined` in the playbook doorway, which sends no `playId`.
   */
  playId?: string;
  /**
   * Plan 2026-09-27-006 U7a — required alongside `playId`: this write runs
   * `ensurePlayOwned` first (Key Technical Decision 3), a no-op when the
   * sequence is already this play's own.
   */
  playbookId?: string;
  /** Plan 2026-09-28-006 U4 — the group this edit came from; required for the in-play gate. */
  usageOrigin?: UsageSidebarOrigin;
  /** The library row's name — the Save menu's title. */
  sequenceName?: string;
  /** The library row's `owningPlayId` (this play's id for a copy). */
  owningPlayId?: string | null;
  /** [R18] The library row is a Library piece archived while this play still uses it. */
  archivedInLibrary?: boolean;
}) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(currentFlowId ?? '');
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      // Re-seed from the stored flow id every time the popover opens, so a
      // cancelled edit never leaks into the next one — unless a Save panel
      // for THIS group and THIS field is still pending, in which case the
      // rep's own unsaved flow id is what they should see (plan
      // 2026-09-29-001 U2, R14; see `settings-draft-restore.ts`).
      const pendingDraft = settingsDraftForReopen(
        useUsageSidebarStore.getState().pending,
        usageOrigin,
        'flow',
      );
      setValue(pendingDraft?.field === 'flow' ? pendingDraft.flowId : (currentFlowId ?? ''));
      setShowError(false);
    }
    setOpen(nextOpen);
  }

  function handleSave() {
    const trimmed = value.trim();
    if (trimmed === '') {
      setShowError(true);
      return;
    }
    setSaving(true);
    // `group` and `playId` are either both present (in play) or both absent
    // (outside one) — matching `SequenceSettingsWriteOptions`'s union exactly.
    const write = (id: string, group?: { rungId: string; confirmedRungIds?: string[] }) =>
      group === undefined
        ? setSequenceFlow(apiFetch, id, trimmed, { playId: undefined })
        : setSequenceFlow(apiFetch, id, trimmed, { playId: playId!, group });
    void (
      playId !== undefined && usageOrigin !== undefined
        ? saveSequenceSettingsWithGate(apiFetch, queryClient, {
            sequenceId,
            sequenceName,
            usageOrigin,
            owningPlayId,
            archivedInLibrary,
            draftSettings: { field: 'flow', flowId: trimmed },
            playId,
            playbookId,
            write,
          })
        : write(sequenceId).then((value) => ({ kind: 'written' as const, value }))
    )
      .then((response) => {
        if (response.kind === 'written') {
          applySequenceFlowIdToCache(queryClient, response.value.id, response.value.flowId);
          onSaved?.(response.value.flowId);
        }
        // `'menu'` — the Save menu took over; the rep's choice does the write.
        // Plan 2026-09-29-001 U2 (R14, B5) — `'stale'` keeps this popover
        // open on the flow id the rep typed.
        if (response.kind !== 'stale') setOpen(false);
      })
      .catch((err: unknown) => {
        addErrorToast({
          content: err instanceof Error ? err.message : 'Could not update the flow ID — try again.',
        });
      })
      .finally(() => {
        setSaving(false);
      });
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {/* `data-row-control` — see `SequenceSenderPopover.tsx`'s own note: Config's
          `RowShell` skips row-expand toggling for anything carrying this attribute. */}
      <PopoverButton variant={variant} size={size} data-row-control>
        {currentFlowId === null ? 'Set flow ID' : 'Change flow ID'}
      </PopoverButton>
      <PopoverPanel positioner={{ side: 'bottom', align: 'start' }}>
        <div className="flex w-72 flex-col gap-4 p-3" data-row-control>
          {usedInPlacesText(usageCount) !== null && (
            <Text size="xs" variant="tertiary">
              {usedInPlacesText(usageCount)}
            </Text>
          )}
          <InputField
            label="Flow ID"
            placeholder="e.g. 1144420067685858387"
            className="font-mono"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            invalid={showError}
            errorMessage={showError ? 'Enter a flow ID.' : undefined}
          />
          <Text size="xs" variant="tertiary">
            The {channelLabel(channel)} sequence ID this Sequence sends through.
          </Text>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" loading={saving} onClick={handleSave}>
              Save flow ID
            </Button>
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
}
